import { FC, useMemo } from 'react';
import { max, sum } from 'd3';

import { zNodeStatus } from '@dbt-labs/dbt-dag';
import {
  Accessor,
  BarStacks,
  CategoryAxis,
  categoryScale,
  InteractionItem,
  LinearAxis,
  linearScale,
  Plot,
  TooltipCallbackArgs,
} from '@dbt-labs/sourdough';

import { useModelTestExecutionHistoryStatus } from '@/hooks/discovery/modelTestExecutionHistoryStatus';
import { JobFilter } from '@/pages/account/project/resource/details/components/Tabs/PerformanceView';
import { getISODateUTCArrayFromDateStrings } from '@/util/date';
import { ChartWrapper } from './ChartWrapper';

export interface ModelTestExecutionStatusStackedBarChartProps {
  environmentId: number;
  startDate: string;
  endDate: string;
  uniqueId: string;
  setOldestDate: (date: Date | undefined) => void;
  onItemClicked?: (date: JobFilter) => void;
}

type GroupedData = {
  date: string;
  executionFailOrErrorRate: number;
  values: [number, number, number, number];
};

export const ModelTestExecutionStatusStackedBarChart: FC<
  ModelTestExecutionStatusStackedBarChartProps
> = ({
  environmentId,
  startDate,
  endDate,
  uniqueId,
  setOldestDate,
  onItemClicked = () => {},
}) => {
  const [testExecutionHistory] = useModelTestExecutionHistoryStatus({
    environmentId,
    startDate,
    endDate,
    uniqueId,
  });
  setOldestDate(undefined);
  const groupedData = useMemo((): GroupedData[] | undefined => {
    const modelTestExecutionHistory =
      testExecutionHistory.data?.performance.modelTestExecutionHistory;
    if (!modelTestExecutionHistory) return undefined;

    const dateArray = getISODateUTCArrayFromDateStrings(startDate, endDate);

    const dayMap = modelTestExecutionHistory.reduce<Record<string, any>>(
      (acc, curr) => {
        const {
          date,
          executionFailOrErrorRate,
          executionPassCount,
          executionWarnCount,
          executionFailCount,
          executionErrorCount,
        } = curr;
        acc[curr.date] = {
          date,
          executionFailOrErrorRate,
          values: [
            executionPassCount,
            executionWarnCount,
            executionFailCount,
            executionErrorCount,
          ],
        };
        return acc;
      },
      {},
    );

    const chartData = dateArray.map((date): GroupedData => {
      const data = dayMap?.[date];
      if (data) {
        return data;
      }
      return {
        date,
        executionFailOrErrorRate: 0.0,
        values: [0, 0, 0, 0],
      };
    });
    if (chartData?.every((x) => x.values.every((y) => y === 0))) {
      return [];
    }
    return chartData;
  }, [
    endDate,
    testExecutionHistory.data?.performance.modelTestExecutionHistory,
    startDate,
  ]);

  const aggregateFailOrErrorRate = useMemo((): number | undefined => {
    const modelTestExecutionHistory =
      testExecutionHistory.data?.performance.modelTestExecutionHistory;
    if (!modelTestExecutionHistory) return undefined;

    const totalTests = sum(
      modelTestExecutionHistory.map(
        (execution) =>
          execution.executionPassCount +
          execution.executionWarnCount +
          execution.executionFailCount +
          execution.executionErrorCount,
      ),
    );
    const totalFailuresOrErrors = sum(
      modelTestExecutionHistory.map(
        (execution) => execution.executionFailCount + execution.executionErrorCount,
      ),
    );

    return totalTests ? totalFailuresOrErrors / totalTests : 0;
  }, [testExecutionHistory.data?.performance.modelTestExecutionHistory]);

  const xAccessor: Accessor<GroupedData, string> = (x: GroupedData) => {
    const [, MM, DD] = x.date.split('-');
    return `${MM}-${DD}`;
  };
  const xScale = categoryScale({
    labels: groupedData?.map(xAccessor) ?? [],
  });

  const yAccessor = (d: GroupedData) => sum(d.values);
  const yScale = linearScale({
    min: 0,
    max: max(groupedData ?? [], yAccessor) ?? 0,
  });

  return (
    <>
      <div className="ml-[10px]">
        <div className="font-sans text-sm font-light leading-6 text-fgAlt">
          Model Test Failure Rate
        </div>
        {aggregateFailOrErrorRate !== undefined && (
          <span className="text-2xl">
            {(aggregateFailOrErrorRate! * 100).toFixed(2)}%
          </span>
        )}
      </div>
      <div className="h-[300px]">
        <ChartWrapper
          testId="model-test-execution-status-stacked-bar-chart"
          numDataPoints={groupedData?.length}
          isLoading={testExecutionHistory.fetching}
          error={testExecutionHistory.error?.message}
          height={300}
          margin={{ bottom: 30, left: 45, right: 5, top: 25 }}
          xAccessor={xAccessor}
          xScale={xScale}
          yAccessor={yAccessor}
          yScale={yScale}
          noDataMessage="No data available"
          tooltip={({ interactionItem }: TooltipCallbackArgs<GroupedData>) => {
            const item = interactionItem?.item;
            if (!item) return null;
            const [passes, warns, failures, errors] = item.values;

            const passStr = passes === 1 ? `1 passing test` : `${passes} passing tests`;
            const warnStr =
              warns === 1 ? `1 test with warnings` : `${warns} tests with warnings`;
            const failStr =
              failures === 1
                ? `1 test with failures`
                : `${failures} tests with failures`;
            const errorStr =
              errors === 1 ? `1 test with errors` : `${errors} tests with errors`;
            const failOrErrorRateStr = `${(item.executionFailOrErrorRate * 100).toFixed(
              2,
            )}% of tests failed or errored`;

            return (
              <div>
                <div>{item.date}</div>
                <div>{passStr}</div>
                <div>{warnStr}</div>
                <div>{failStr}</div>
                <div>{errorStr}</div>
                <div>{failOrErrorRateStr}</div>
              </div>
            );
          }}
          onItemClick={({ item, subItem }: InteractionItem<GroupedData>) => {
            const statusResult = zNodeStatus.safeParse(
              (subItem as any)?.subPlotName?.toLowerCase(),
            );
            const status = statusResult.success ? statusResult.data : undefined;
            // TODO: Warn does not exist in this endpoint, it's treated as success!
            onItemClicked?.({
              ...item,
              status: status === 'warn' ? 'success' : status,
            });
          }}
          colors={[
            // TODO: choose appropriate colors
            {
              fill: 'fill-fgVizEmerald',
              legendIconColor: 'bg-fgVizEmerald',
              unfocused: 'opacity-20 stroke-opacity-20',
              focused: '',
            },
            {
              fill: 'fill-fgVizAmber',
              legendIconColor: 'bg-fgVizAmber',
              unfocused: 'opacity-20 stroke-opacity-20',
              focused: '',
            },
            {
              fill: 'fill-fgVizRed',
              legendIconColor: 'bg-fgVizRed',
              unfocused: 'opacity-20 stroke-opacity-20',
              focused: '',
            },
            {
              fill: 'fill-fgVizOrange',
              legendIconColor: 'bg-fgVizOrange',
              unfocused: 'opacity-20 stroke-opacity-20',
              focused: '',
            },
          ]}
          legendProps={{
            width: 75,
            position: 'right',
          }}
        >
          <LinearAxis axis="y" label="# of Executions with a Test" />
          <CategoryAxis axis="x" label="Test Execution Date" />
          <Plot
            data={groupedData ?? []}
            itemComponent={BarStacks}
            extraItemProps={{
              groupValuesAccessor: (item: any): number[] => item.values,
            }}
            name={['Success', 'Warn', 'Failed', 'Error']}
          />
        </ChartWrapper>
      </div>
    </>
  );
};
